import { watch } from 'vue';
import { useAnalytics } from '@satellite/composables/useAnalytics';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { useAuthStore } from '@/store/useAuthStore';
import { useWarehouseStore } from '@/store/useWarehouseStore';
import type { IMeResponse } from '@/types/apiTypes';

export function useCirceAnalytics() {
  const { trackYardTaskWarehouseChanged } = useYardTaskAnalytics();
  const analytics = useAnalytics();
  const authStore = useAuthStore();
  const warehouseStore = useWarehouseStore();

  const registerOrgSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }

    analytics.registerSuper({
      org_id: user.orgId ?? '',
      org_name: user.orgName ?? ''
    });
  };

  const registerWarehouseSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }

    const selectedWarehouseId = warehouseStore.selectedWarehouseId;
    const selectedWarehouseName = warehouseStore.selectedWarehouseName;

    analytics.registerSuper({
      warehouse_id: String(selectedWarehouseId ?? ''),
      warehouse_name: String(selectedWarehouseName ?? '')
    });
  };

  const registerUserSuperProperty = () => {
    const user = authStore.user as IMeResponse | null;
    if (!user) {
      return;
    }
    analytics.setUserProperties({
      $distinct_id: user.id ?? '',
      $email: user.email ?? '',
      $name: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
      user_role: user.role ?? '',
      org_id: user.orgId ?? '',
      org_name: user.orgName ?? ''
    });
  };

  watch(
    () => authStore.user,
    user => {
      if (user) {
        analytics.identify(user.id);
        registerOrgSuperProperty();
        registerWarehouseSuperProperty();
        registerUserSuperProperty();
      } else {
        // Fires with a null user on every page load too, before getMe resolves.
        // MixpanelPlugin.reset() no-ops until identify() has succeeded, so only a
        // real logout reaches the SDK.
        analytics.reset();
      }
    },
    { immediate: true }
  );

  watch(
    () => warehouseStore.selectedWarehouseId,
    () => {
      registerWarehouseSuperProperty();
      trackYardTaskWarehouseChanged();
    }
  );
}
